import { createSelector } from 'reselect';

import { EXTENSIONS, FEATURES } from 'constants/RightsConstants';
import { getUserAccessMatrix } from 'modules/users/selectors/currentUser';

import { getProps, getRoot, getSecondProps } from 'utils/selectors';

/** @return {import("modules/groups/reducers").GroupsReducer} */
export const getState = state => state.newGroups;

const sortByGroupName = (a, b) => a.name.localeCompare(b.name);

// ============================================================================
// Data Selectors
// ============================================================================

const getDataSlice = createSelector(getState, state => state.data);

/** Get all group entities as a dictionary */
export const getGroupEntities = createSelector(getDataSlice, data => data.entities);

/** Get all group IDs */
export const getGroupIds = createSelector(getDataSlice, data => data.ids);

/** Get a group by its ID */
export const getGroupById = createSelector(getGroupEntities, getProps, (entities, id) => entities?.[id]);

export const getGroupByIds = createSelector(
  getGroupEntities,
  getProps,
  /** @param {string[]} groupIds */
  (entities, groupIds = []) => groupIds.map(groupId => entities[groupId]).filter(Boolean)
);

// ============================================================================
// UI Selectors
// ============================================================================

export const getUISlice = createSelector(getState, state => state.ui);

/** Get the selected group ID */
export const getSelectedGroupId = createSelector(getUISlice, ui => ui.selectedGroupId);

/** Get the selected group */
export const getSelectedGroup = createSelector(getGroupEntities, getSelectedGroupId, (entities, id) => entities?.[id]);

// Details
const getDetailsSlice = createSelector(getUISlice, ui => ui.details);

/** Check if a group is being created */
export const getGroupsIsCreating = createSelector(getDetailsSlice, details => details.creating);

/** Check if a group is being updated */
export const getGroupsIsUpdating = createSelector(getDetailsSlice, details => details.updating);

// Group Config
const getGroupConfigSlice = createSelector(getUISlice, ui => ui.groupConfig);

/** Check if group config is ready */
export const getGroupConfigIsReady = createSelector(getGroupConfigSlice, config => config.ready);

/** Check if group config is initialized */
export const getGroupConfigIsInitialized = createSelector(getGroupConfigSlice, config => config.initialized);

/** Check if group config is assigning */
export const isGroupConfigAssigning = createSelector(getGroupConfigSlice, config => config.assigning);

export const getGroupManagementReady = createSelector(getUISlice, ui => ui.groupManagement.ready);

// Select (SimpleSearchReducer)
const getSelectSlice = createSelector(getUISlice, ui => ui.select);

/** Get select IDs */
export const getGroupsSelectIds = createSelector(getSelectSlice, select => select.ids);

/** Get select loading state */
export const getGroupsIsFetching = createSelector(getSelectSlice, select => select.loading);

/** Get select error */
export const getGroupsSelectError = createSelector(getSelectSlice, select => select.error);

/** Get select page */
export const getGroupsSelectPage = createSelector(getSelectSlice, select => select.page);

/** Check if there are more groups to load */
export const getGroupsSelectHasMore = createSelector(getSelectSlice, select => select.hasMore);

/** Get groups from select IDs (for dropdown) */
export const getGroupsSelect = createSelector(getRoot, getGroupsSelectIds, getGroupByIds);

/** Get all groups from select as array */
export const getAllGroupsSelect = createSelector(
  getGroupEntities,
  getGroupsSelectIds,
  (entities, ids) => ids?.map(id => entities?.[id]).filter(Boolean) ?? []
);

/** Get group name by ID from select */
export const getGroupSelectNameById = createSelector(
  getGroupEntities,
  getProps,
  (entities, id) => entities?.[id]?.name
);

// By Extension
const getByExtensionSlice = createSelector(getUISlice, ui => ui.byExtension);

/** Get group IDs by extension */
export const getGroupIdsByExtension = createSelector(
  getByExtensionSlice,
  getProps,
  (byExtension, extension) => byExtension[extension] ?? []
);

/** Get groups by extension (sorted by name) */
export const getGroupsByExtension = createSelector(
  getGroupEntities,
  getGroupIdsByExtension,
  (entities, groupIds) => groupIds?.flatMap(id => entities?.[id] ?? []).sort(sortByGroupName) ?? []
);

// ============================================================================
// Derived Selectors
// ============================================================================

/** Get extensions of the selected group */
export const getSelectedGroupExtensions = createSelector(getSelectedGroup, group => group?.extensions);

/** Get currency ISO code of a group by ID */
export const getGroupCurrencyIsoCode = createSelector(getGroupById, group => group?.currency);

/** Get currency ISO code of the selected group */
export const getSelectedGroupCurrencyIsoCode = createSelector(getRoot, getSelectedGroupId, getGroupCurrencyIsoCode);

/** Get integrations of the selected group */
export const getSelectedGroupIntegrations = createSelector(getSelectedGroup, group => group?.integrations ?? []);

/** Get access matrix for the selected group */
export const getGroupAccessMatrix = createSelector(
  getUserAccessMatrix,
  getSelectedGroup,
  (userRightsMatrix, group) => ({
    ...userRightsMatrix,
    [FEATURES]: group?.features ?? [],
    [EXTENSIONS]: group?.extensions ?? [],
  })
);

/** Get access matrix for the selected group */
export const hasExtensions = createSelector(
  getGroupById,
  getSecondProps,
  (group, extension) => group?.extensions?.includes(extension) ?? false
);

export const getFeaturesByGroupId = createSelector(getGroupById, group => group?.features ?? []);

/** Get the features of the selected group */
export const getSelectedGroupFeatures = createSelector(getSelectedGroup, group => group?.features ?? []);

/** Whether the selected group has a given feature enabled. A feature present on a group implies at least one of its dealers has it. */
export const selectedGroupHasFeature = createSelector(
  getSelectedGroupFeatures,
  getProps,
  (features, feature) => features.includes(feature)
);
